import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { SecureStorage } from '@/lib/sessionManager';
import { 
  Bell, Mail, Clock, Calendar, Settings, Send, RefreshCw, 
  CheckCircle, XCircle, AlertTriangle, Loader2, Play, Pause,
  BarChart3, Users, FileText, Truck, AlertCircle, Server, Eye, EyeOff, Trash2,
  Lock, Shield, Activity, UserPlus, X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NotificationSettings = () => {
  const [notificationTypes, setNotificationTypes] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [scheduledJobs, setScheduledJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testEmail, setTestEmail] = useState('');
  
  // Modal de configuration des destinataires
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [configForm, setConfigForm] = useState({
    enabled: true,
    frequency: 'daily',
    recipients: {
      roles: [],
      external_emails: []
    }
  });
  const [newExternalEmail, setNewExternalEmail] = useState('');
  const [savingConfig, setSavingConfig] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotal, setLogsTotal] = useState(0);
  
  // SMTP Config state
  const [smtpConfig, setSmtpConfig] = useState(null);
  const [smtpForm, setSmtpForm] = useState({
    enabled: true,
    host: '',
    port: 465,
    user: '',
    password: '',
    from_email: '',
    from_name: 'Baobab Connect',
    security: 'ssl'  // ssl, starttls, none
  });
  const [showPassword, setShowPassword] = useState(false);
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [smtpTestEmail, setSmtpTestEmail] = useState('');
  const [testingSmtp, setTestingSmtp] = useState(false);
  
  // SMTP Diagnostic & Logs state
  const [smtpDiagnosis, setSmtpDiagnosis] = useState(null);
  const [diagnosing, setDiagnosing] = useState(false);
  const [smtpLogs, setSmtpLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  const getHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${SecureStorage.getToken()}`
  });

  // Charger les données initiales au montage
  useEffect(() => {
    loadDataInternal();
  }, []);

  const loadDataInternal = async () => {
    setLoading(true);
    try {
      // Charger les données séquentiellement pour éviter les problèmes
      await loadNotificationTypes();
      await loadConfigs();
      await loadLogs(1);
      await loadStats();
      await loadScheduledJobs();
      await loadSmtpConfig();
    } catch (error) {
      console.error('Erreur chargement données:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };
  
  const loadData = () => {
    loadDataInternal();
  };

  const loadNotificationTypes = async () => {
    try {
      const res = await fetch(`${API_URL}/api/notifications/types`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setNotificationTypes(data.types || []);
      } else {
        console.error('Erreur loadNotificationTypes:', res.status);
      }
    } catch (err) {
      console.error('loadNotificationTypes error:', err);
    }
  };

  const loadConfigs = async () => {
    try {
      const res = await fetch(`${API_URL}/api/notifications/config`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setConfigs(data);
      } else {
        console.error('Erreur loadConfigs:', res.status);
      }
    } catch (err) {
      console.error('loadConfigs error:', err);
    }
  };

  const loadLogs = async (page = 1) => {
    try {
      const res = await fetch(`${API_URL}/api/notifications/logs?page=${page}&limit=10`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setLogs(data.items || []);
        setLogsTotal(data.total || 0);
        setLogsPage(page);
      } else {
        console.error('Erreur loadLogs:', res.status);
      }
    } catch (err) {
      console.error('loadLogs error:', err);
    }
  };

  const loadStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/notifications/stats`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      } else {
        console.error('Erreur loadStats:', res.status);
      }
    } catch (err) {
      console.error('loadStats error:', err);
    }
  };

  const loadScheduledJobs = async () => {
    try {
      const res = await fetch(`${API_URL}/api/notifications/scheduler/jobs`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setScheduledJobs(data.jobs || []);
      } else {
        console.error('Erreur loadScheduledJobs:', res.status);
      }
    } catch (err) {
      console.error('loadScheduledJobs error:', err);
    }
  };

  // =========================================================================
  // Fonctions SMTP
  // =========================================================================
  
  const loadSmtpConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/api/notifications/smtp/config`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setSmtpConfig(data);
        // Pré-remplir le formulaire
        setSmtpForm({
          enabled: data.enabled,
          host: data.host || '',
          port: data.port || 465,
          user: data.user || '',
          password: '', // Ne jamais pré-remplir le mot de passe
          from_email: data.from_email || '',
          from_name: data.from_name || 'Baobab Connect',
          security: data.security || 'ssl'
        });
      }
    } catch (err) {
      console.error('loadSmtpConfig error:', err);
    }
  };

  const saveSmtpConfig = async () => {
    setSavingSmtp(true);
    try {
      // Ne pas envoyer le mot de passe si l'utilisateur ne l'a pas modifié (chaîne vide)
      const dataToSend = { ...smtpForm };
      if (!dataToSend.password || dataToSend.password.trim() === '') {
        dataToSend.password = null;  // null = ne pas changer le mot de passe existant
      }
      
      const res = await fetch(`${API_URL}/api/notifications/smtp/config`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(dataToSend)
      });

      if (res.ok) {
        const data = await res.json();
        setSmtpConfig(data);
        toast.success('Configuration SMTP enregistrée');
        // Vider le champ mot de passe après sauvegarde
        setSmtpForm(prev => ({ ...prev, password: '' }));
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur lors de la sauvegarde');
      }
    } catch (err) {
      console.error('saveSmtpConfig error:', err);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingSmtp(false);
    }
  };

  const resetSmtpConfig = async () => {
    if (!confirm('Supprimer la configuration SMTP de la base de données ? Les paramètres .env seront utilisés.')) {
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/notifications/smtp/config`, {
        method: 'DELETE',
        headers: getHeaders()
      });

      if (res.ok) {
        toast.success('Configuration SMTP réinitialisée');
        await loadSmtpConfig();
      } else {
        toast.error('Erreur lors de la réinitialisation');
      }
    } catch (err) {
      console.error('resetSmtpConfig error:', err);
      toast.error('Erreur lors de la réinitialisation');
    }
  };

  const testSmtpConnection = async () => {
    if (!smtpTestEmail) {
      toast.error('Veuillez entrer une adresse email de test');
      return;
    }

    setTestingSmtp(true);
    try {
      const res = await fetch(`${API_URL}/api/notifications/smtp/test`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ email: smtpTestEmail })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Email de test envoyé ! (source: ${data.smtp_source})`);
        loadLogs(1);
        loadStats();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Échec du test SMTP');
      }
    } catch (err) {
      console.error('testSmtpConnection error:', err);
      toast.error('Erreur lors du test');
    } finally {
      setTestingSmtp(false);
    }
  };

  // Diagnostic SMTP
  const runSmtpDiagnosis = async () => {
    setDiagnosing(true);
    setSmtpDiagnosis(null);
    try {
      const res = await fetch(`${API_URL}/api/notifications/smtp/diagnose`, {
        method: 'POST',
        headers: getHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        setSmtpDiagnosis(data);
        if (data.overall_status === 'ok') {
          toast.success('Diagnostic SMTP réussi !');
        } else if (data.overall_status === 'disabled') {
          toast.info('SMTP est désactivé');
        } else {
          toast.error('Problème détecté dans la configuration SMTP');
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur lors du diagnostic');
      }
    } catch (err) {
      console.error('runSmtpDiagnosis error:', err);
      toast.error('Erreur lors du diagnostic');
    } finally {
      setDiagnosing(false);
    }
  };

  // Charger les logs SMTP
  const loadSmtpLogs = async () => {
    setLoadingLogs(true);
    try {
      const res = await fetch(`${API_URL}/api/notifications/smtp/logs?limit=50`, {
        headers: getHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        setSmtpLogs(data.logs || []);
      }
    } catch (err) {
      console.error('loadSmtpLogs error:', err);
    } finally {
      setLoadingLogs(false);
    }
  };

  // Debug SMTP complet
  const [smtpDebug, setSmtpDebug] = useState(null);
  const [debuggingSmtp, setDebuggingSmtp] = useState(false);
  
  const runSmtpDebug = async () => {
    setDebuggingSmtp(true);
    setSmtpDebug(null);
    try {
      const res = await fetch(`${API_URL}/api/notifications/smtp/debug`, {
        headers: getHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        setSmtpDebug(data);
      } else {
        toast.error('Erreur lors du debug SMTP');
      }
    } catch (err) {
      console.error('runSmtpDebug error:', err);
      toast.error('Erreur lors du debug');
    } finally {
      setDebuggingSmtp(false);
    }
  };

  // Ouvrir le modal de configuration
  const openConfigModal = (notificationType) => {
    const typeInfo = notificationTypes.find(t => t.value === notificationType);
    const existingConfig = configs.find(c => c.notification_type === notificationType);
    
    setEditingConfig({
      type: notificationType,
      label: typeInfo?.label || notificationType,
      category: typeInfo?.category
    });
    
    setConfigForm({
      enabled: existingConfig?.enabled ?? true,
      frequency: existingConfig?.frequency || typeInfo?.default_frequency || 'daily',
      recipients: {
        roles: existingConfig?.recipients?.roles || ['Admin'],
        external_emails: existingConfig?.recipients?.external_emails || []
      }
    });
    
    setNewExternalEmail('');
    setConfigModalOpen(true);
  };

  // Ajouter un email externe
  const addExternalEmail = () => {
    if (!newExternalEmail || !newExternalEmail.includes('@')) {
      toast.error('Email invalide');
      return;
    }
    if (configForm.recipients.external_emails.includes(newExternalEmail)) {
      toast.error('Email déjà ajouté');
      return;
    }
    setConfigForm(prev => ({
      ...prev,
      recipients: {
        ...prev.recipients,
        external_emails: [...prev.recipients.external_emails, newExternalEmail]
      }
    }));
    setNewExternalEmail('');
  };

  // Retirer un email externe
  const removeExternalEmail = (email) => {
    setConfigForm(prev => ({
      ...prev,
      recipients: {
        ...prev.recipients,
        external_emails: prev.recipients.external_emails.filter(e => e !== email)
      }
    }));
  };

  // Toggle un rôle
  const toggleRole = (role) => {
    setConfigForm(prev => {
      const roles = prev.recipients.roles.includes(role)
        ? prev.recipients.roles.filter(r => r !== role)
        : [...prev.recipients.roles, role];
      return {
        ...prev,
        recipients: { ...prev.recipients, roles }
      };
    });
  };

  // Sauvegarder la configuration complète
  const saveFullConfig = async () => {
    if (!editingConfig) return;
    
    setSavingConfig(true);
    try {
      const configData = {
        notification_type: editingConfig.type,
        enabled: configForm.enabled,
        recipients: configForm.recipients,
        frequency: configForm.frequency,
        schedule_time: '08:00'
      };

      const res = await fetch(`${API_URL}/api/notifications/config`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(configData)
      });

      if (res.ok) {
        toast.success('Configuration sauvegardée');
        setConfigModalOpen(false);
        loadConfigs();
      } else {
        throw new Error('Erreur de sauvegarde');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingConfig(false);
    }
  };

  // Mettre à jour une configuration (simple toggle)
  const updateConfig = async (notificationType, enabled, recipients = null) => {
    try {
      const existingConfig = configs.find(c => c.notification_type === notificationType);
      
      const configData = {
        notification_type: notificationType,
        enabled: enabled,
        recipients: recipients || existingConfig?.recipients || {
          roles: ['Admin'],
          specific_users: [],
          external_emails: []
        },
        frequency: existingConfig?.frequency || 'realtime',
        schedule_time: existingConfig?.schedule_time || '08:00'
      };

      const res = await fetch(`${API_URL}/api/notifications/config`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(configData)
      });

      if (res.ok) {
        toast.success(`Configuration mise à jour`);
        loadConfigs();
      } else {
        throw new Error('Erreur de mise à jour');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Envoyer un email de test
  const sendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Veuillez entrer une adresse email');
      return;
    }

    setSendingTest(true);
    try {
      const res = await fetch(`${API_URL}/api/notifications/test`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ email: testEmail, template: 'test' })
      });

      if (res.ok) {
        toast.success(`Email de test envoyé à ${testEmail}`);
        setTestDialogOpen(false);
        setTestEmail('');
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Erreur lors de l\'envoi');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de l\'envoi du test');
    } finally {
      setSendingTest(false);
    }
  };

  // Déclencher un rapport manuellement
  const triggerReport = async (reportType) => {
    try {
      const res = await fetch(`${API_URL}/api/notifications/send-report`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ report_type: reportType })
      });

      if (res.ok) {
        toast.success(`Rapport ${reportType} envoyé avec succès`);
        loadLogs(1);
        loadStats();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Erreur lors de l\'envoi du rapport');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de l\'envoi du rapport');
    }
  };

  // Obtenir l'icône selon le type
  const getTypeIcon = (type) => {
    const icons = {
      operation_created: <Truck className="h-4 w-4" />,
      operation_status_changed: <RefreshCw className="h-4 w-4" />,
      operation_completed: <CheckCircle className="h-4 w-4" />,
      incident_created: <AlertTriangle className="h-4 w-4" />,
      incident_critical: <AlertCircle className="h-4 w-4" />,
      document_expiring: <FileText className="h-4 w-4" />,
      user_created: <Users className="h-4 w-4" />,
      daily_report: <Calendar className="h-4 w-4" />,
      weekly_report: <BarChart3 className="h-4 w-4" />,
      monthly_report: <BarChart3 className="h-4 w-4" />,
      document_alert: <AlertTriangle className="h-4 w-4" />
    };
    return icons[type] || <Bell className="h-4 w-4" />;
  };

  // Obtenir le statut de configuration
  const getConfigStatus = (type) => {
    const config = configs.find(c => c.notification_type === type);
    return config?.enabled ?? false;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="notification-settings-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bell className="h-6 w-6" />
            Notifications & Rapports
          </h1>
          <p className="text-muted-foreground mt-1">
            Configurez les notifications automatiques et les rapports périodiques
          </p>
        </div>
        <div className="flex gap-2">
          <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="test-email-btn">
                <Send className="h-4 w-4 mr-2" />
                Envoyer un test
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Envoyer un email de test</DialogTitle>
                <DialogDescription>
                  Vérifiez que la configuration SMTP fonctionne correctement.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="test-email">Adresse email</Label>
                  <Input
                    id="test-email"
                    type="email"
                    placeholder="votre@email.com"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    data-testid="test-email-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setTestDialogOpen(false)}>
                  Annuler
                </Button>
                <Button onClick={sendTestEmail} disabled={sendingTest} data-testid="send-test-btn">
                  {sendingTest ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Envoyer
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button onClick={loadData} variant="ghost" data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Statistiques */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Dernières 24h</p>
                  <p className="text-2xl font-bold">{stats.last_24h?.sent || 0}</p>
                </div>
                <Mail className="h-8 w-8 text-primary opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Échecs 24h</p>
                  <p className="text-2xl font-bold text-red-500">{stats.last_24h?.failed || 0}</p>
                </div>
                <XCircle className="h-8 w-8 text-red-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">7 derniers jours</p>
                  <p className="text-2xl font-bold">{stats.last_7d?.sent || 0}</p>
                </div>
                <BarChart3 className="h-8 w-8 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">30 derniers jours</p>
                  <p className="text-2xl font-bold">{stats.last_30d?.sent || 0}</p>
                </div>
                <Calendar className="h-8 w-8 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="events" className="space-y-4">
        <TabsList>
          <TabsTrigger value="events" data-testid="tab-events">
            <Bell className="h-4 w-4 mr-2" />
            Événements
          </TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports">
            <BarChart3 className="h-4 w-4 mr-2" />
            Rapports
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <Clock className="h-4 w-4 mr-2" />
            Historique
          </TabsTrigger>
          <TabsTrigger value="scheduler" data-testid="tab-scheduler">
            <Settings className="h-4 w-4 mr-2" />
            Planification
          </TabsTrigger>
          <TabsTrigger value="smtp" data-testid="tab-smtp">
            <Server className="h-4 w-4 mr-2" />
            SMTP
          </TabsTrigger>
        </TabsList>

        {/* Tab Événements */}
        <TabsContent value="events" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Notifications Événementielles</CardTitle>
              <CardDescription>
                Ces notifications sont envoyées en temps réel lorsqu&apos;un événement se produit.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {notificationTypes
                  .filter(t => t.category === 'event')
                  .map(type => {
                    const config = configs.find(c => c.notification_type === type.value);
                    const recipientCount = (config?.recipients?.roles?.length || 0) + (config?.recipients?.external_emails?.length || 0);
                    
                    return (
                    <div 
                      key={type.value} 
                      className="flex items-center justify-between p-4 border rounded-lg"
                      data-testid={`config-${type.value}`}
                    >
                      <div className="flex items-center gap-3">
                        {getTypeIcon(type.value)}
                        <div>
                          <p className="font-medium">{type.label}</p>
                          <p className="text-sm text-muted-foreground">
                            {recipientCount > 0 ? `${recipientCount} destinataire(s)` : 'Aucun destinataire configuré'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => openConfigModal(type.value)}
                        >
                          <Settings className="h-4 w-4 mr-1" />
                          Configurer
                        </Button>
                        <Badge variant={getConfigStatus(type.value) ? 'default' : 'secondary'}>
                          {getConfigStatus(type.value) ? 'Activé' : 'Désactivé'}
                        </Badge>
                        <Switch
                          checked={getConfigStatus(type.value)}
                          onCheckedChange={(checked) => updateConfig(type.value, checked)}
                          data-testid={`switch-${type.value}`}
                        />
                      </div>
                    </div>
                  )})}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Rapports */}
        <TabsContent value="reports" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Rapports Périodiques</CardTitle>
              <CardDescription>
                Ces rapports sont envoyés automatiquement selon la fréquence configurée.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {notificationTypes
                  .filter(t => t.category === 'report')
                  .map(type => {
                    const config = configs.find(c => c.notification_type === type.value);
                    const recipientCount = (config?.recipients?.roles?.length || 0) + (config?.recipients?.external_emails?.length || 0);
                    const frequency = config?.frequency || type.default_frequency;
                    
                    return (
                    <div 
                      key={type.value} 
                      className="flex items-center justify-between p-4 border rounded-lg"
                      data-testid={`config-${type.value}`}
                    >
                      <div className="flex items-center gap-3">
                        {getTypeIcon(type.value)}
                        <div>
                          <p className="font-medium">{type.label}</p>
                          <p className="text-sm text-muted-foreground">
                            Fréquence: {frequency === 'daily' ? 'Quotidien' : 
                                       frequency === 'weekly' ? 'Hebdomadaire' : 
                                       frequency === 'monthly' ? 'Mensuel' : frequency}
                            {' • '}
                            {recipientCount > 0 ? `${recipientCount} destinataire(s)` : 'Aucun destinataire'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => openConfigModal(type.value)}
                        >
                          <Settings className="h-4 w-4 mr-1" />
                          Configurer
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => triggerReport(frequency)}
                          data-testid={`trigger-${type.value}`}
                        >
                          <Send className="h-4 w-4 mr-1" />
                          Envoyer
                        </Button>
                        <Badge variant={getConfigStatus(type.value) ? 'default' : 'secondary'}>
                          {getConfigStatus(type.value) ? 'Activé' : 'Désactivé'}
                        </Badge>
                        <Switch
                          checked={getConfigStatus(type.value)}
                          onCheckedChange={(checked) => updateConfig(type.value, checked)}
                          data-testid={`switch-${type.value}`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Historique */}
        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Historique des notifications</CardTitle>
              <CardDescription>
                {logsTotal} notifications envoyées
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Sujet</TableHead>
                    <TableHead>Destinataires</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                        Aucune notification envoyée
                      </TableCell>
                    </TableRow>
                  ) : (
                    logs.map((log) => (
                      <TableRow key={log.id} data-testid={`log-${log.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getTypeIcon(log.notification_type)}
                            <span className="text-sm">{log.notification_type}</span>
                          </div>
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate">
                          {log.subject}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {log.recipients?.length || 0} dest.
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {log.status === 'sent' ? (
                            <Badge className="bg-green-500">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Envoyé
                            </Badge>
                          ) : log.status === 'failed' ? (
                            <Badge variant="destructive">
                              <XCircle className="h-3 w-3 mr-1" />
                              Échec
                            </Badge>
                          ) : (
                            <Badge variant="secondary">
                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                              En cours
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(log.created_at).toLocaleString('fr-FR')}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              
              {/* Pagination */}
              {logsTotal > 10 && (
                <div className="flex justify-center gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => loadLogs(logsPage - 1)}
                    disabled={logsPage === 1}
                  >
                    Précédent
                  </Button>
                  <span className="py-2 px-4 text-sm">
                    Page {logsPage} / {Math.ceil(logsTotal / 10)}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => loadLogs(logsPage + 1)}
                    disabled={logsPage >= Math.ceil(logsTotal / 10)}
                  >
                    Suivant
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Scheduler */}
        <TabsContent value="scheduler" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Tâches Planifiées</CardTitle>
              <CardDescription>
                Liste des tâches automatiques configurées
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Planification</TableHead>
                    <TableHead>Prochaine exécution</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {scheduledJobs
                    .filter(job => ['daily_report', 'weekly_report', 'monthly_report', 'document_alerts'].includes(job.id))
                    .map((job) => (
                      <TableRow key={job.id} data-testid={`job-${job.id}`}>
                        <TableCell className="font-medium">
                          {job.id === 'daily_report' ? 'Rapport Quotidien' :
                           job.id === 'weekly_report' ? 'Rapport Hebdomadaire' :
                           job.id === 'monthly_report' ? 'Rapport Mensuel' :
                           job.id === 'document_alerts' ? 'Alertes Documents' : job.id}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {job.trigger}
                        </TableCell>
                        <TableCell>
                          {job.next_run ? new Date(job.next_run).toLocaleString('fr-FR') : 'N/A'}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab SMTP */}
        <TabsContent value="smtp" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Server className="h-5 w-5" />
                Configuration SMTP
              </CardTitle>
              <CardDescription>
                Configurez les paramètres du serveur de messagerie pour l&apos;envoi des emails.
                {smtpConfig && (
                  <Badge variant={smtpConfig.source === 'database' ? 'default' : 'secondary'} className="ml-2">
                    Source: {smtpConfig.source === 'database' ? 'Base de données' : 'Fichier .env'}
                  </Badge>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-6">
                {/* Statut */}
                <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
                  <div className="flex items-center gap-3">
                    <Mail className="h-5 w-5 text-primary" />
                    <div>
                      <p className="font-medium">Envoi d&apos;emails</p>
                      <p className="text-sm text-muted-foreground">
                        {smtpForm.enabled ? 'Les notifications seront envoyées par email' : 'Les emails sont désactivés'}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={smtpForm.enabled}
                    onCheckedChange={(checked) => setSmtpForm(prev => ({ ...prev, enabled: checked }))}
                    data-testid="smtp-enabled-switch"
                  />
                </div>

                {/* Formulaire SMTP */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="smtp-host">Hôte SMTP</Label>
                    <Input
                      id="smtp-host"
                      placeholder="smtp.example.com"
                      value={smtpForm.host}
                      onChange={(e) => setSmtpForm(prev => ({ ...prev, host: e.target.value }))}
                      data-testid="smtp-host-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="smtp-port">Port</Label>
                    <Input
                      id="smtp-port"
                      type="number"
                      placeholder="465"
                      value={smtpForm.port}
                      onChange={(e) => setSmtpForm(prev => ({ ...prev, port: parseInt(e.target.value) || 465 }))}
                      data-testid="smtp-port-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="smtp-user">Utilisateur</Label>
                    <Input
                      id="smtp-user"
                      placeholder="user@example.com"
                      value={smtpForm.user}
                      onChange={(e) => setSmtpForm(prev => ({ ...prev, user: e.target.value }))}
                      data-testid="smtp-user-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="smtp-password">Mot de passe</Label>
                    <div className="relative">
                      <Input
                        id="smtp-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder={smtpConfig?.password_set ? '••••••••' : 'Entrer le mot de passe'}
                        value={smtpForm.password}
                        onChange={(e) => setSmtpForm(prev => ({ ...prev, password: e.target.value }))}
                        data-testid="smtp-password-input"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                    {smtpConfig?.password_set && (
                      <p className="text-xs text-muted-foreground">Laissez vide pour conserver le mot de passe actuel</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="smtp-from-email">Mail From</Label>
                    <Input
                      id="smtp-from-email"
                      placeholder="notifications@example.com"
                      value={smtpForm.from_email}
                      onChange={(e) => setSmtpForm(prev => ({ ...prev, from_email: e.target.value }))}
                      data-testid="smtp-from-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="smtp-from-name">Nom expéditeur</Label>
                    <Input
                      id="smtp-from-name"
                      placeholder="Baobab Connect"
                      value={smtpForm.from_name}
                      onChange={(e) => setSmtpForm(prev => ({ ...prev, from_name: e.target.value }))}
                      data-testid="smtp-from-name-input"
                    />
                  </div>
                </div>

                {/* Security Type Selection */}
                <div className="p-4 border rounded-lg space-y-3">
                  <div>
                    <p className="font-medium">Type de sécurité</p>
                    <p className="text-sm text-muted-foreground">
                      Choisissez le protocole de sécurité pour la connexion SMTP
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      type="button"
                      variant={smtpForm.security === 'ssl' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSmtpForm(prev => ({ ...prev, security: 'ssl' }))}
                      data-testid="smtp-security-ssl"
                    >
                      <Lock className="h-4 w-4 mr-2" />
                      SSL/TLS
                    </Button>
                    <Button
                      type="button"
                      variant={smtpForm.security === 'starttls' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSmtpForm(prev => ({ ...prev, security: 'starttls' }))}
                      data-testid="smtp-security-starttls"
                    >
                      <Shield className="h-4 w-4 mr-2" />
                      STARTTLS
                    </Button>
                    <Button
                      type="button"
                      variant={smtpForm.security === 'none' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSmtpForm(prev => ({ ...prev, security: 'none' }))}
                      data-testid="smtp-security-none"
                    >
                      <AlertTriangle className="h-4 w-4 mr-2" />
                      Aucune
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {smtpForm.security === 'ssl' && 'SSL/TLS établit une connexion chiffrée dès le départ (généralement port 465)'}
                    {smtpForm.security === 'starttls' && 'STARTTLS démarre en clair puis passe en mode chiffré (généralement port 587)'}
                    {smtpForm.security === 'none' && '⚠️ Connexion non sécurisée - à éviter en production (généralement port 25)'}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-4 pt-4 border-t">
                  <Button onClick={saveSmtpConfig} disabled={savingSmtp} data-testid="save-smtp-btn">
                    {savingSmtp ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                    Enregistrer
                  </Button>
                  
                  <div className="flex gap-2">
                    <Input
                      placeholder="Email de test"
                      value={smtpTestEmail}
                      onChange={(e) => setSmtpTestEmail(e.target.value)}
                      className="w-48"
                      data-testid="smtp-test-email-input"
                    />
                    <Button 
                      variant="outline" 
                      onClick={testSmtpConnection} 
                      disabled={testingSmtp || !smtpTestEmail}
                      data-testid="test-smtp-btn"
                    >
                      {testingSmtp ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                      Tester
                    </Button>
                  </div>

                  {smtpConfig?.source === 'database' && (
                    <Button variant="destructive" size="sm" onClick={resetSmtpConfig} data-testid="reset-smtp-btn">
                      <Trash2 className="h-4 w-4 mr-2" />
                      Réinitialiser (utiliser .env)
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Diagnostic SMTP */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Activity className="h-5 w-5" />
                Diagnostic SMTP
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Button 
                  onClick={runSmtpDiagnosis}
                  disabled={diagnosing}
                  data-testid="diagnose-smtp-btn"
                >
                  {diagnosing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Activity className="h-4 w-4 mr-2" />}
                  Lancer le diagnostic
                </Button>
                <Button 
                  variant="outline"
                  onClick={loadSmtpLogs}
                  disabled={loadingLogs}
                  data-testid="load-smtp-logs-btn"
                >
                  {loadingLogs ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
                  Voir les logs
                </Button>
                <Button 
                  variant="secondary"
                  onClick={runSmtpDebug}
                  disabled={debuggingSmtp}
                  data-testid="debug-smtp-btn"
                >
                  {debuggingSmtp ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Server className="h-4 w-4 mr-2" />}
                  Debug complet
                </Button>
              </div>

              {/* Résultats du diagnostic */}
              {smtpDiagnosis && (
                <div className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">Résultats du diagnostic</h4>
                    <Badge 
                      variant={smtpDiagnosis.overall_status === 'ok' ? 'default' : 
                               smtpDiagnosis.overall_status === 'disabled' ? 'secondary' : 'destructive'}
                    >
                      {smtpDiagnosis.overall_status === 'ok' ? '✅ OK' : 
                       smtpDiagnosis.overall_status === 'disabled' ? '⚠️ Désactivé' : '❌ Erreur'}
                    </Badge>
                  </div>
                  
                  <div className="text-xs text-muted-foreground">
                    {smtpDiagnosis.timestamp}
                  </div>

                  <div className="space-y-2">
                    {smtpDiagnosis.tests?.map((test, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        <span className={
                          test.status === 'ok' ? 'text-green-600' : 
                          test.status === 'warning' ? 'text-yellow-600' : 'text-red-600'
                        }>
                          {test.status === 'ok' ? '✅' : test.status === 'warning' ? '⚠️' : '❌'}
                        </span>
                        <span className="font-medium">{test.name}:</span>
                        <span className="text-muted-foreground">{test.message}</span>
                      </div>
                    ))}
                  </div>

                  {smtpDiagnosis.config && (
                    <div className="mt-4 p-3 bg-muted rounded text-xs font-mono">
                      <div>Host: {smtpDiagnosis.config.host}:{smtpDiagnosis.config.port}</div>
                      <div>Security: {smtpDiagnosis.config.security}</div>
                      <div>User: {smtpDiagnosis.config.user}</div>
                      <div>From: {smtpDiagnosis.config.from_email}</div>
                    </div>
                  )}
                </div>
              )}

              {/* Logs SMTP */}
              {smtpLogs.length > 0 && (
                <div className="border rounded-lg p-4 space-y-2">
                  <h4 className="font-medium flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Logs SMTP ({smtpLogs.length})
                  </h4>
                  <div className="max-h-64 overflow-y-auto space-y-1 text-xs font-mono bg-slate-950 text-slate-100 p-3 rounded">
                    {smtpLogs.map((log, idx) => (
                      <div 
                        key={idx} 
                        className={
                          log.level === 'error' ? 'text-red-400' :
                          log.level === 'warning' ? 'text-yellow-400' :
                          log.level === 'success' ? 'text-green-400' : 'text-slate-300'
                        }
                      >
                        {log.timestamp && <span className="text-slate-500">[{log.timestamp}] </span>}
                        {log.raw?.replace(/\u001b\[\d+m/g, '')}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Debug SMTP Complet */}
              {smtpDebug && (
                <div className="border rounded-lg p-4 space-y-4">
                  <h4 className="font-medium flex items-center gap-2">
                    <Server className="h-4 w-4" />
                    Debug SMTP Complet
                  </h4>
                  
                  {/* Configuration active */}
                  <div className="space-y-2">
                    <h5 className="text-sm font-medium text-muted-foreground">Configuration Active (source: {smtpDebug.active_config?.source})</h5>
                    <div className="grid grid-cols-2 gap-2 text-xs bg-muted p-3 rounded font-mono">
                      <div>Host: {smtpDebug.active_config?.host}</div>
                      <div>Port: {smtpDebug.active_config?.port}</div>
                      <div>Security: {smtpDebug.active_config?.security}</div>
                      <div>User: {smtpDebug.active_config?.user}</div>
                      <div>Password: {smtpDebug.active_config?.password_set ? `[SET - ${smtpDebug.active_config?.password_length} chars]` : '❌ NOT SET'}</div>
                      <div>Enabled: {smtpDebug.active_config?.enabled ? '✅' : '❌'}</div>
                    </div>
                  </div>

                  {/* Tests réseau */}
                  <div className="space-y-2">
                    <h5 className="text-sm font-medium text-muted-foreground">Tests Réseau</h5>
                    <div className="space-y-1 text-sm">
                      <div className="flex items-center gap-2">
                        <span>{smtpDebug.network_test?.dns?.status === 'ok' ? '✅' : '❌'}</span>
                        <span>DNS: {smtpDebug.network_test?.dns?.status === 'ok' ? smtpDebug.network_test?.dns?.ip : smtpDebug.network_test?.dns?.error}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span>{smtpDebug.network_test?.port?.status === 'ok' ? '✅' : '❌'}</span>
                        <span>Port {smtpDebug.active_config?.port}: {smtpDebug.network_test?.port?.status === 'ok' ? 'Accessible' : smtpDebug.network_test?.port?.error || 'Non accessible'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Test SMTP */}
                  <div className="space-y-2">
                    <h5 className="text-sm font-medium text-muted-foreground">Test SMTP</h5>
                    <div className="space-y-1 text-sm">
                      <div className="flex items-center gap-2">
                        <span>{smtpDebug.smtp_test?.connection === 'ok' ? '✅' : '❌'}</span>
                        <span>Connexion: {smtpDebug.smtp_test?.connection === 'ok' ? 'OK' : JSON.stringify(smtpDebug.smtp_test?.connection)}</span>
                      </div>
                      {smtpDebug.smtp_test?.authentication && (
                        <div className="flex items-center gap-2">
                          <span>{smtpDebug.smtp_test?.authentication === 'ok' ? '✅' : '❌'}</span>
                          <span>Authentification: {smtpDebug.smtp_test?.authentication === 'ok' ? 'OK' : JSON.stringify(smtpDebug.smtp_test?.authentication)}</span>
                        </div>
                      )}
                      {smtpDebug.smtp_test?.skipped && (
                        <div className="text-yellow-600">⚠️ {smtpDebug.smtp_test?.skipped}</div>
                      )}
                    </div>
                  </div>

                  {/* Recommandations */}
                  {smtpDebug.recommendations?.length > 0 && (
                    <div className="space-y-2">
                      <h5 className="text-sm font-medium text-red-600">Recommandations</h5>
                      <ul className="list-disc list-inside text-sm text-red-600 space-y-1">
                        {smtpDebug.recommendations.map((rec, idx) => (
                          <li key={idx}>{rec}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Variables d'environnement */}
                  <details className="text-xs">
                    <summary className="cursor-pointer text-muted-foreground">Variables d'environnement (.env)</summary>
                    <pre className="mt-2 p-2 bg-slate-950 text-slate-100 rounded overflow-x-auto">
{JSON.stringify(smtpDebug.environment, null, 2)}
                    </pre>
                  </details>

                  {/* Config DB */}
                  <details className="text-xs">
                    <summary className="cursor-pointer text-muted-foreground">Configuration en base de données</summary>
                    <pre className="mt-2 p-2 bg-slate-950 text-slate-100 rounded overflow-x-auto">
{JSON.stringify(smtpDebug.database, null, 2)}
                    </pre>
                  </details>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal de configuration des destinataires */}
      <Dialog open={configModalOpen} onOpenChange={setConfigModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Configurer: {editingConfig?.label}</DialogTitle>
            <DialogDescription>
              Définissez les destinataires et la fréquence de cette notification.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Activation */}
            <div className="flex items-center justify-between">
              <Label>Notification activée</Label>
              <Switch
                checked={configForm.enabled}
                onCheckedChange={(checked) => setConfigForm(prev => ({ ...prev, enabled: checked }))}
              />
            </div>

            {/* Fréquence (seulement pour les rapports) */}
            {editingConfig?.category === 'report' && (
              <div className="space-y-2">
                <Label>Fréquence d'envoi</Label>
                <Select
                  value={configForm.frequency}
                  onValueChange={(value) => setConfigForm(prev => ({ ...prev, frequency: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner une fréquence" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Quotidien (08h00)</SelectItem>
                    <SelectItem value="weekly">Hebdomadaire (Lundi 08h00)</SelectItem>
                    <SelectItem value="monthly">Mensuel (1er du mois 08h00)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Destinataires par rôle */}
            <div className="space-y-3">
              <Label>Destinataires par rôle</Label>
              <div className="space-y-2">
                {['SuperAdmin', 'Admin', 'Manager', 'Operator'].map(role => (
                  <div key={role} className="flex items-center space-x-2">
                    <Checkbox
                      id={`role-${role}`}
                      checked={configForm.recipients.roles.includes(role)}
                      onCheckedChange={() => toggleRole(role)}
                    />
                    <label
                      htmlFor={`role-${role}`}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {role === 'SuperAdmin' ? 'Super Admin' : 
                       role === 'Admin' ? 'Administrateur' :
                       role === 'Manager' ? 'Manager' : 'Opérateur'}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Emails externes */}
            <div className="space-y-3">
              <Label>Emails externes</Label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="email@exemple.com"
                  value={newExternalEmail}
                  onChange={(e) => setNewExternalEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addExternalEmail()}
                />
                <Button type="button" variant="outline" onClick={addExternalEmail}>
                  <UserPlus className="h-4 w-4" />
                </Button>
              </div>
              
              {configForm.recipients.external_emails.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {configForm.recipients.external_emails.map(email => (
                    <Badge key={email} variant="secondary" className="flex items-center gap-1">
                      {email}
                      <X 
                        className="h-3 w-3 cursor-pointer hover:text-destructive" 
                        onClick={() => removeExternalEmail(email)}
                      />
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigModalOpen(false)}>
              Annuler
            </Button>
            <Button onClick={saveFullConfig} disabled={savingConfig}>
              {savingConfig ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NotificationSettings;
